import { useState } from "react";
import { Check, Link, X } from "lucide-react";
import { toast } from "sonner";
import { MediaSourcesField } from "@/components/agent/details/MediaSourcesField";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Badge } from "@/components/ui/badge";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { describeMediaSources, readMediaSourcesConfig } from "@/lib/agent/mediaSources";
import { Agent, MediaSourcesConfig } from "@/types/agent/agent";
import { SHOW_RELATED_MEDIA_TOOL_ID, ToolList } from "@/utils/config/elevenlabs";

interface ExternalToolsCardProps {
  agent: Agent;
  brand?: string;
}

export const ExternalToolsCard = ({ agent, brand = "#4E9CFF" }: ExternalToolsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const enabledToolIds: string[] = agent.settings?.conversation_config?.agent?.prompt?.tool_ids ?? [];
  const syncTools = agent.settings?.sync_tools ?? [];

  const savedMediaSources = readMediaSourcesConfig(agent.settings?.media_sources);

  const [selectedTools, setSelectedTools] = useState<string[]>(enabledToolIds);
  const [mediaSources, setMediaSources] = useState<MediaSourcesConfig>(savedMediaSources);
  const updateAgent = useUpdateAgent();

  const handleEdit = () => {
    setSelectedTools(agent.settings?.conversation_config?.agent?.prompt?.tool_ids ?? []);
    setMediaSources(savedMediaSources);
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    const showsMedia = selectedTools.includes(SHOW_RELATED_MEDIA_TOOL_ID);
    if (showsMedia && mediaSources.mode === "approved_sites" && mediaSources.allowed_domains.length === 0) {
      toast.error("Add at least one approved site, or choose another option.");
      return;
    }

    await updateAgent.mutateAsync({
      agent,
      updateData: {
        settings: {
          ...agent.settings,
          // Switching the tool off keeps what was saved for when it comes back on.
          media_sources: showsMedia ? mediaSources : savedMediaSources,
          conversation_config: {
            ...agent.settings?.conversation_config,
            agent: {
              ...agent.settings?.conversation_config?.agent,
              prompt: {
                ...agent.settings?.conversation_config?.agent?.prompt,
                tool_ids: selectedTools,
              },
            },
          },
        },
      },
    });
    toast.success("Tools saved");
    setIsEditing(false);
  };

  const toggleTool = (id: string, checked: boolean) => {
    setSelectedTools((prev) => (checked ? [...prev, id] : prev.filter((t) => t !== id)));
  };

  return (
    <OverviewSectionCard
      title="External Tools"
      subtitle="Capabilities your genie can use during conversations"
      genieIcon="wrench"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      <div className="space-y-2">
        {isEditing && <p className="text-xs text-gray-500 mb-3">Select tools to enable for this genie</p>}
        {!isEditing && (
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline">
              {ToolList.filter((tool) => enabledToolIds.includes(tool.id)).length + syncTools.length} /{" "}
              {ToolList.length + syncTools.length} enabled
            </Badge>
          </div>
        )}
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {ToolList.map((tool) => {
            const isSelected = isEditing ? selectedTools.includes(tool.id) : enabledToolIds.includes(tool.id);
            return isEditing ? (
              <label
                key={tool.id}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  isSelected ? "bg-blue-50 border-blue-200" : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => toggleTool(tool.id, e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900">{tool.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{tool.description}</div>
                </div>
              </label>
            ) : (
              <div
                key={tool.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  isSelected ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200 opacity-60"
                }`}
              >
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-500" : "bg-gray-300"}`}
                >
                  {isSelected ? <Check className="w-3 h-3 text-white" /> : <X className="w-3 h-3 text-gray-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium ${isSelected ? "text-gray-900" : "text-gray-600"}`}>
                    {tool.name}
                  </div>
                  <div className={`text-xs mt-0.5 ${isSelected ? "text-gray-600" : "text-gray-500"}`}>
                    {tool.description}
                  </div>
                  {isSelected && tool.id === SHOW_RELATED_MEDIA_TOOL_ID && (
                    <div className="text-xs mt-1 font-medium text-gray-700 break-words">
                      Looks: {describeMediaSources(savedMediaSources)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {syncTools.map((tool) => (
            <div
              key={tool.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-purple-100 bg-purple-50/50"
            >
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                <Link className="w-3 h-3 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{tool.name}</span>
                  <Badge className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100">
                    Managed by Sync
                  </Badge>
                </div>
                {tool.description && <div className="text-xs text-gray-500 mt-0.5">{tool.description}</div>}
              </div>
            </div>
          ))}
        </div>

        {isEditing && selectedTools.includes(SHOW_RELATED_MEDIA_TOOL_ID) && (
          <div className="pt-2">
            <MediaSourcesField agentId={agent.id} value={mediaSources} onChange={setMediaSources} brand={brand} />
          </div>
        )}
      </div>
    </OverviewSectionCard>
  );
};
